import type { SVGProps } from "react";
import { useTranslate } from "@refinedev/core";

import type { Page } from "../../shell/nav";
import "./foster-error.css";

// fix/foster-login-redesign — the foster portal's friendly error state (replaces the shared ErrorState
// on the portal screens), matching the "Oeps, er ging iets technisch mis" mockup: the puppy+kitten
// illustration, a retry + back-to-dashboard action, a paw divider and a contact link. Admin / self-service
// keep the shared <ErrorState>. `onNavigate` (optional) drives the dashboard + contact links.

function RefreshIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v5h-5" />
    </svg>
  );
}
function HomeIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M3 10.5 12 4l9 6.5M5 9.5V20h14V9.5M9.5 20v-6h5v6" />
    </svg>
  );
}
function ChatIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M21 11.5a8.4 8.4 0 0 1-11.9 7.6L3 21l1.9-6.1A8.4 8.4 0 1 1 21 11.5z" />
    </svg>
  );
}
function PawMark(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden="true" {...p}>
      <circle cx="6" cy="11" r="1.7" />
      <circle cx="10" cy="6.5" r="1.7" />
      <circle cx="14" cy="6.5" r="1.7" />
      <circle cx="18" cy="11" r="1.7" />
      <path d="M12 12.2c-2.4 0-4.6 1.9-4.6 4 0 1.6 1.3 2.6 3 2.3.6-.1 1-.3 1.6-.3s1 .2 1.6.3c1.7.3 3-.7 3-2.3 0-2.1-2.2-4-4.6-4z" />
    </svg>
  );
}

export function FosterError({
  onRetry,
  onNavigate,
}: {
  onRetry?: () => void;
  onNavigate?: (page: Page) => void;
}) {
  const t = useTranslate();
  return (
    <div className="foster-error">
      <PawMark className="fe-bg-paw fe-bg-paw--1" />
      <PawMark className="fe-bg-paw fe-bg-paw--2" />
      <PawMark className="fe-bg-paw fe-bg-paw--3" />
      <PawMark className="fe-bg-paw fe-bg-paw--4" />

      <div className="fe-card" role="alert">
        <img className="fe-illustration" src="/foster-error-pets.png" alt="" />
        <h1 className="fe-title">{t("portal.error.title")}</h1>
        <p className="fe-subtitle">
          {t("portal.error.body1")}
          <br />
          {t("portal.error.body2")}
        </p>

        {onRetry ? (
          <button className="fe-btn fe-btn--primary" type="button" onClick={onRetry}>
            <span>{t("portal.error.retry")}</span>
            <RefreshIcon />
          </button>
        ) : null}
        {onNavigate ? (
          <button className="fe-btn fe-btn--outline" type="button" onClick={() => onNavigate("dashboard")}>
            <HomeIcon />
            <span>{t("portal.error.home")}</span>
          </button>
        ) : null}

        <div className="fe-divider" aria-hidden="true">
          <span className="fe-divider-line" />
          <PawMark className="fe-divider-paw" />
          <span className="fe-divider-line" />
        </div>

        <p className="fe-footer">
          <ChatIcon />
          <span>
            {t("portal.error.footerPrefix")}{" "}
            {onNavigate ? (
              <button className="fe-link" type="button" onClick={() => onNavigate("contact")}>
                {t("portal.error.contactLink")}
              </button>
            ) : (
              <span className="fe-link">{t("portal.error.contactLink")}</span>
            )}
            .
          </span>
        </p>
      </div>
    </div>
  );
}
